import React, { useState, useEffect } from 'react';
import { X, Info, AlertTriangle, XCircle } from 'lucide-react';
import { supabase } from '@/lib/supabaseClient';
import { useSession } from '@/providers/AuthProvider';

interface SystemAnnouncement {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'warning' | 'critical';
  active: boolean;
  expires_at: string | null;
}

export function SystemAlertBanner() {
  const { user } = useSession();
  const [announcement, setAnnouncement] = useState<SystemAnnouncement | null>(null);
  const [dismissed, setDismissed] = useState<string[]>([]);

  useEffect(() => {
    if (!user) return;

    const fetchActiveAnnouncement = async () => {
      try {
        // maybeSingle: 0 rader är normalfallet — .single() gav 406 i konsolen
        const { data, error } = await supabase
          .from('system_announcements')
          .select('*')
          .eq('active', true)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (error) {
          // PGRST205 = table doesn't exist, which is fine (table may not be created yet)
          if (error.code !== 'PGRST205') {
            console.error('Error fetching announcement:', error);
          }
          return;
        }

        if (data) {
          // Check if announcement has expired
          if (data.expires_at && new Date(data.expires_at) < new Date()) {
            // Auto-deactivate expired announcements
            await supabase
              .from('system_announcements')
              .update({ active: false })
              .eq('id', data.id);
            return;
          }

          // Check if user has dismissed this announcement
          const dismissedKey = `announcement_dismissed_${data.id}`;
          const isDismissed = localStorage.getItem(dismissedKey) === 'true';
          
          if (!isDismissed) {
            setAnnouncement(data);
          }
        }
      } catch (error) {
        console.error('Error fetching announcement:', error);
      }
    };

    fetchActiveAnnouncement();

    // Channel skapas synkront och städas i effect-cleanup — cleanup inuti
    // en .then() körs aldrig och gav dubbel-subscribe under StrictMode
    const channel = supabase
      .channel(`system_announcements_${user.id}_${Date.now()}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'system_announcements',
        },
        () => {
          fetchActiveAnnouncement();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const handleDismiss = () => {
    if (!announcement) return;
    
    const dismissedKey = `announcement_dismissed_${announcement.id}`;
    localStorage.setItem(dismissedKey, 'true');
    setDismissed([...dismissed, announcement.id]);
    setAnnouncement(null);
  };

  if (!announcement || dismissed.includes(announcement.id)) {
    return null;
  }

  const getStyles = () => {
    switch (announcement.type) {
      case 'critical':
        return {
          bg: 'bg-red-600',
          border: 'border-red-700',
          text: 'text-white',
          icon: <XCircle className="h-5 w-5" />,
        };
      case 'warning':
        return {
          bg: 'bg-yellow-500',
          border: 'border-yellow-600',
          text: 'text-yellow-900',
          icon: <AlertTriangle className="h-5 w-5" />,
        };
      case 'info':
      default:
        return {
          bg: 'bg-blue-600',
          border: 'border-blue-700',
          text: 'text-white',
          icon: <Info className="h-5 w-5" />,
        };
    }
  };

  const styles = getStyles();

  return (
    <div className={`${styles.bg} ${styles.border} border-b ${styles.text} z-50`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 flex-1">
            <div className="flex-shrink-0 mt-0.5">
              {styles.icon}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-sm sm:text-base mb-1">
                {announcement.title}
              </h3>
              <p className="text-sm opacity-90 leading-relaxed">
                {announcement.message}
              </p>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className={`flex-shrink-0 ${styles.text} hover:opacity-70 transition-opacity p-1`}
            aria-label="Luk meddelelse"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
